"use client";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import type { Editor } from "@tiptap/react";
import { TextSelection } from "@tiptap/pm/state";
import { ChevronDown, ChevronUp, HelpCircle } from "lucide-react";
import {
    forwardRef,
    KeyboardEvent,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from "react";
import {
    findAllRanges,
    replaceAll,
    wordMarkForRange,
    type MatchRange,
} from "./lib/replaceUtils";
import { scrollPosIntoSurface } from "./lib/scroll";
import { cn } from "@/lib/utils";
import { t } from "@/lib/localization";

type Props = {
    editor: Editor | null;
    onOpenHelp: () => void;
};

export type FindReplacePanelHandle = {
    setFindText: (text: string) => void;
    focusFind: () => void;
};

const FindReplacePanel = forwardRef<FindReplacePanelHandle, Props>(
    function FindReplacePanel({ editor, onOpenHelp }, ref) {
        const [find, setFind] = useState("");
        const [replace, setReplace] = useState("");
        const [wholeWord, setWholeWord] = useState(false);
        const [currentIdx, setCurrentIdx] = useState(0);
        const [docVersion, setDocVersion] = useState(0);
        const findInputRef = useRef<HTMLInputElement>(null);

        useEffect(() => {
            if (!editor) return;
            const handler = ({
                transaction,
            }: {
                transaction: { docChanged: boolean };
            }) => {
                if (transaction.docChanged) setDocVersion((v) => v + 1);
            };
            editor.on("update", handler);
            return () => {
                editor.off("update", handler);
            };
        }, [editor]);

        useImperativeHandle(
            ref,
            () => ({
                setFindText: (text: string) => {
                    setFind(text);
                    setTimeout(() => {
                        findInputRef.current?.focus();
                        findInputRef.current?.select();
                    }, 0);
                },
                focusFind: () => {
                    findInputRef.current?.focus();
                    findInputRef.current?.select();
                },
            }),
            []
        );

        const matches: MatchRange[] = useMemo(() => {
            if (!editor || !find) return [];
            return findAllRanges(editor, find, {
                caseSensitive: false,
                wholeWord,
            });
            // docVersion is intentional — recompute matches whenever the doc changes.
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [editor, find, wholeWord, docVersion]);

        useEffect(() => {
            if (currentIdx >= matches.length) setCurrentIdx(0);
        }, [matches.length, currentIdx]);

        // Clear any stale selection that no longer corresponds to a match
        // (e.g. after toggling whole-word so the previously highlighted partial
        // match should no longer appear selected).
        useEffect(() => {
            if (!editor) return;
            const sel = editor.state.selection;
            if (sel.empty) return;
            const stillValid = matches.some(
                (m) => m.from === sel.from && m.to === sel.to
            );
            if (stillValid) return;
            const tr = editor.state.tr.setSelection(
                TextSelection.create(editor.state.doc, sel.from, sel.from)
            );
            editor.view.dispatch(tr);
        }, [editor, matches]);

        const go = (delta: number) => {
            if (!editor || matches.length === 0) return;
            const next = (currentIdx + delta + matches.length) % matches.length;
            setCurrentIdx(next);
            const { from, to } = matches[next];
            const tr = editor.state.tr.setSelection(
                TextSelection.create(editor.state.doc, from, to)
            );
            editor.view.focus();
            editor.view.dispatch(tr);
            scrollPosIntoSurface(editor.view, from);
        };

        const onFindKey = (e: KeyboardEvent<HTMLInputElement>) => {
            if (e.key === "Enter") {
                e.preventDefault();
                go(e.shiftKey ? -1 : 1);
            } else if (e.key === "Escape") {
                e.preventDefault();
            }
        };

        const onReplaceKey = (e: KeyboardEvent<HTMLInputElement>) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                handleReplaceAll();
            } else if (e.key === "Enter") {
                e.preventDefault();
                handleReplaceOne();
            } else if (e.key === "Escape") {
                e.preventDefault();
            }
        };

        const handleReplaceOne = () => {
            if (!editor || matches.length === 0) return;
            const target = matches[currentIdx];
            if (!target) return;
            const tr = editor.state.tr;
            if (replace.length > 0) {
                const mark = wordMarkForRange(editor, target.from, target.to);
                tr.replaceWith(
                    target.from,
                    target.to,
                    editor.schema.text(replace, mark ? [mark] : null)
                );
            } else {
                tr.delete(target.from, target.to);
            }
            editor.view.dispatch(tr);
        };

        const handleReplaceAll = () => {
            if (!editor) return;
            replaceAll(editor, find, replace, {
                caseSensitive: false,
                wholeWord,
            });
        };

        if (!open) return null;

        return (
            <div className="flex flex-col gap-2 rounded-md border border-border bg-background p-2 shadow-sm">
                <div className="flex flex-col gap-2 xl:flex-row xl:items-center xl:justify-between">
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-1">
                        <div className="flex flex-row items-center gap-1">
                            <Input
                                ref={findInputRef}
                                className="h-7 w-24 md:w-28 lg:w-32 text-xs"
                                placeholder={t(
                                    "video-sheet.captions-tab.editor.find.placeholder"
                                )}
                                value={find}
                                onChange={(e) => setFind(e.target.value)}
                                onKeyDown={onFindKey}
                            />
                            <Input
                                className="h-7 w-24 md:w-28 lg:w-32 text-xs"
                                placeholder={t(
                                    "video-sheet.captions-tab.editor.replace.placeholder"
                                )}
                                value={replace}
                                onChange={(e) => setReplace(e.target.value)}
                                onKeyDown={onReplaceKey}
                            />
                        </div>
                        <div className="flex flex-row items-center gap-1">
                            <div className="flex items-center gap-1 text-xs">
                                <button
                                    type="button"
                                    onClick={() => setWholeWord((v) => !v)}
                                    className={cn(
                                        "rounded border px-2 py-1 transition-colors",
                                        wholeWord
                                            ? "border-primary bg-primary/10 text-foreground"
                                            : "border-border text-muted-foreground hover:bg-muted"
                                    )}
                                    title={t(
                                        "video-sheet.captions-tab.editor.whole-word"
                                    )}
                                >
                                    W
                                </button>
                            </div>
                            <button
                                type="button"
                                onClick={() => go(1)}
                                disabled={matches.length === 0}
                                aria-label={t(
                                    "video-sheet.captions-tab.editor.next-match"
                                )}
                                className="p-0 text-muted-foreground hover:text-foreground disabled:opacity-50"
                            >
                                <ChevronDown className="size-3.5" />
                            </button>
                            <span
                                dir="ltr"
                                className="whitespace-nowrap text-xs text-muted-foreground"
                            >
                                {matches.length === 0
                                    ? "0 / 0"
                                    : `${currentIdx + 1} / ${matches.length}`}
                            </span>
                            <button
                                type="button"
                                onClick={() => go(-1)}
                                disabled={matches.length === 0}
                                aria-label={t(
                                    "video-sheet.captions-tab.editor.previous-match"
                                )}
                                className="p-0 text-muted-foreground hover:text-foreground disabled:opacity-50"
                            >
                                <ChevronUp className="size-3.5" />
                            </button>
                        </div>
                    </div>

                    <div className="flex items-center gap-1">
                        <Button
                            type="button"
                            size="sm"
                            variant="secondary"
                            className="h-7 px-2 text-xs"
                            onClick={handleReplaceOne}
                            disabled={matches.length === 0}
                        >
                            {t("video-sheet.captions-tab.editor.replace")}
                        </Button>
                        <Button
                            type="button"
                            size="sm"
                            variant="secondary"
                            className="h-7 px-2 text-xs"
                            onClick={handleReplaceAll}
                            disabled={matches.length === 0}
                        >
                            {t("video-sheet.captions-tab.editor.replace-all")}
                        </Button>
                        <button
                            type="button"
                            onClick={onOpenHelp}
                            aria-label={t(
                                "video-sheet.captions-tab.editor.shortcuts"
                            )}
                            title={t(
                                "video-sheet.captions-tab.editor.shortcuts-title"
                            )}
                            className="p-1 text-muted-foreground transition-colors hover:text-foreground"
                        >
                            <HelpCircle className="size-4" />
                        </button>
                    </div>
                </div>
            </div>
        );
    }
);

export default FindReplacePanel;
